import type { FC, ReactNode } from 'react'
import type { GeometryKey } from '@/constants/bike'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

import {
    FRAME_SIZES,
    GEOMETRY,
    GEOMETRY_ANGLES,
    GEOMETRY_ORDER
} from '@/constants/bike'

const DEFAULT_SIZE_INDEX = FRAME_SIZES.indexOf('56')

const formatValue = (key: GeometryKey, value: number): string => {
    return GEOMETRY_ANGLES.includes(key) ? `${value}°` : String(value)
}

const GeometrySection: FC = (): ReactNode => {
    const t = useDictionary()
    const { geometry } = t
    const [selected, setSelected] = useState(DEFAULT_SIZE_INDEX)

    return (
        <section id='geometry' aria-labelledby='geometry-title' className='border-t border-border py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end'>
                    <Reveal>
                        <SectionHeading id='geometry-title' eyebrow={geometry.eyebrow} title={geometry.title} intro={geometry.intro} />
                    </Reveal>

                    <div role='group' aria-label={geometry.highlightLabel} className='flex flex-wrap gap-2'>
                        {FRAME_SIZES.map((size, index) => {
                            const active = index === selected

                            return (
                                <button
                                    key={size}
                                    type='button'
                                    aria-pressed={active}
                                    onClick={() => setSelected(index)}
                                    className={cn(
                                        'h-11 min-w-12 rounded-full border px-4 text-sm font-semibold tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                                        active ? 'border-race bg-race text-white' : 'border-border text-muted-foreground hover:border-white/40 hover:text-foreground'
                                    )}
                                >
                                    {size}
                                </button>
                            )
                        })}
                    </div>
                </div>

                <Reveal className='mt-10'>
                    <div className='overflow-x-auto rounded-2xl border border-border'>
                        <table className='w-full min-w-[46rem] border-collapse text-sm'>
                            <caption className='sr-only'>
                                {geometry.caption}
                            </caption>

                            <thead>
                                <tr className='border-b border-border'>
                                    <th scope='col' className='sticky left-0 z-10 bg-card px-5 py-4 text-left font-mono text-[11px] font-normal uppercase tracking-[0.18em] text-muted-foreground'>
                                        {geometry.sizeLabel}
                                    </th>

                                    {FRAME_SIZES.map((size, index) => (
                                        <th
                                            key={size}
                                            scope='col'
                                            className={cn(
                                                'px-4 py-4 text-center font-heading text-xl font-semibold tabular-nums transition-colors duration-200',
                                                index === selected ? 'bg-race text-white' : 'bg-card text-foreground'
                                            )}
                                        >
                                            {size}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {GEOMETRY_ORDER.map((key) => (
                                    <tr key={key} className='border-b border-border last:border-b-0'>
                                        <th scope='row' className='sticky left-0 z-10 whitespace-nowrap bg-background px-5 py-3.5 text-left font-medium text-foreground/90'>
                                            {geometry.rows[key]}
                                        </th>

                                        {GEOMETRY[key].map((value, index) => (
                                            <td
                                                key={FRAME_SIZES[index]}
                                                className={cn(
                                                    'px-4 py-3.5 text-center tabular-nums transition-colors duration-200',
                                                    index === selected ? 'bg-race/15 font-semibold text-foreground' : 'text-muted-foreground'
                                                )}
                                            >
                                                {formatValue(key, value)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Reveal>

                <p className='mt-4 text-xs text-muted-foreground'>
                    {geometry.note}
                </p>
            </div>
        </section>
    )
}

export default GeometrySection
